/**
 * Plain-language area names and one-line control explanations for the guidance table and charts.
 * National IT content (no firm data). Draft until the guidance reviewer (Andres Eulacio) signs it off.
 */
export const PLAIN_LANGUAGE_STATUS = 'draft';
export const AREA_NAMES = {
    '1': 'Devices',
    '2': 'Software',
    '3': 'Data',
    '4': 'Secure setup',
    '5': 'Accounts',
    '6': 'Access',
    '7': 'Patching',
    '8': 'Activity logs',
    '9': 'Email and web',
    '10': 'Malware',
    '11': 'Backups',
    '12': 'Network',
    '14': 'Training',
    '15': 'Suppliers',
    '17': 'Incident response',
    // Plain name for partner and Board pages; the guidance table still shows the E8 codes.
    E8: 'Key protections',
};
export const CONTROL_SUMMARIES = {
    '1.1': 'Keep an up-to-date list of every computer, phone and device that touches firm data.',
    '1.2': 'Find devices that should not be on the network and remove or block them each week.',
    '2.1': 'Keep a list of the software installed across the firm and why it is used.',
    '2.2': 'Use only software the vendor still supports with security updates.',
    '2.3': 'Find unapproved software and remove it or record why it is allowed.',
    '3.1': 'Have a written process for how firm and client data is handled, kept and destroyed.',
    '3.2': 'Know what sensitive data the firm holds and where it lives.',
    '3.3': 'Give people access only to the data their role needs.',
    '3.4': 'Keep data only as long as required, then delete it.',
    '3.5': 'Destroy data securely when it is no longer needed, including on old equipment.',
    '3.6': 'Encrypt laptops and phones so lost devices do not expose data.',
    '4.1': 'Set up computers and servers to a documented secure standard.',
    '4.2': 'Set up firewalls, routers and switches to a documented secure standard.',
    '4.3': 'Lock screens automatically when devices are left unattended.',
    '4.4': 'Run and manage a firewall on every server.',
    '4.5': 'Run and manage a firewall on every laptop and desktop.',
    '4.6': 'Manage devices and systems only through secure methods.',
    '4.7': 'Change or disable the default accounts and passwords that come with systems.',
    '5.1': 'Keep a list of every user, admin and service account.',
    '5.2': 'Use a unique password for every account.',
    '5.3': 'Disable accounts nobody has used in 45 days.',
    '5.4': 'Give administrators a separate account for admin work only.',
    '6.1': 'Follow a set process to grant access when people join or change roles.',
    '6.2': 'Remove access promptly when people leave or change roles.',
    '6.3': 'Require multi-factor sign-in for anything reachable from the internet.',
    '6.4': 'Require multi-factor sign-in for remote access to the network.',
    '6.5': 'Require multi-factor sign-in for all administrator access.',
    '7.1': 'Have a written process for finding and fixing security weaknesses.',
    '7.2': 'Fix known weaknesses on a set timeline, most serious first.',
    '7.3': 'Apply operating system updates automatically.',
    '7.4': 'Apply application updates automatically.',
    '8.1': 'Have a written process for recording and reviewing system activity.',
    '8.2': 'Record activity on important systems so incidents can be traced.',
    '8.3': 'Keep enough storage for activity records to cover an investigation.',
    '9.1': 'Use only supported, up-to-date web browsers and email programs.',
    '9.2': 'Block known harmful websites before staff reach them.',
    '10.1': 'Run anti-malware protection on every device.',
    '10.2': 'Update anti-malware protection automatically.',
    '10.3': 'Stop USB drives and discs from running programs on their own.',
    '11.1': 'Have a written plan for restoring data after a failure or attack.',
    '11.2': 'Back up important data automatically.',
    '11.3': 'Protect backups as carefully as the original data.',
    '11.4': 'Keep a copy of backups separate from the main network, safe from ransomware.',
    '12.1': 'Keep network equipment updated with supported software.',
    '14.1': 'Run an ongoing security awareness program for all staff.',
    '14.2': 'Teach staff to spot phishing and other scams.',
    '14.3': 'Teach staff good password and sign-in habits.',
    '14.4': 'Teach staff how to handle and share sensitive data.',
    '14.5': 'Teach staff how data gets exposed by accident.',
    '14.6': 'Teach staff to recognise and report a security incident.',
    '14.7': 'Teach staff to report devices that are missing updates.',
    '14.8': 'Teach staff the risks of public Wi-Fi and insecure networks.',
    '15.1': 'Keep a list of IT and service providers that handle firm data.',
    '17.1': 'Name the people who lead the response to a security incident.',
    '17.2': 'Keep contact details ready for reporting incidents, inside and outside the firm.',
    '17.3': 'Give staff a clear way to report suspected incidents.',
    'E8-PATCH-APPS': 'Patch applications quickly, and replace ones no longer supported.',
    'E8-PATCH-OS': 'Patch operating systems quickly, and replace ones no longer supported.',
    'E8-OFFICE-MACROS': 'Block Office macros for staff who do not need them.',
    'E8-APP-HARDENING': 'Stop web browsers from running risky content such as Java.',
    'E8-MFA': 'Require multi-factor sign-in for staff and outside access to firm systems.',
    'E8-APP-CONTROL': 'Allow only approved programs to run on staff devices.',
    'E8-BACKUPS': 'Back up critical data on a schedule and test that it restores.'
};
export const areaName = (domainNumber, fallback = domainNumber) => AREA_NAMES[domainNumber] ?? fallback;
/** The two named baselines (Andres, 2026-09-22). Percentages always carry one of these names. */
export const BASELINES = {
    btc: { name: 'BTC Baseline Compliance', what: 'of the 56 national baseline controls in place, fully or partly', size: 56 },
    bti: { name: 'BTI Baseline', what: 'of the 39 BTI requirements (Waves 1 and 2) in place, fully or partly', size: 39 },
};
/** The six maturity levels in plain words, for labels and the Explained panel (CDW's scale, RAS 5.4). */
export const MATURITY_LEVELS = [
    { level: 0, name: 'Non-existent', plain: 'Nothing in place yet.' },
    { level: 1, name: 'Initial', plain: 'Done case by case, when someone remembers.' },
    { level: 2, name: 'Managed', plain: 'Usually done the same way, but not written down.' },
    { level: 3, name: 'Defined', plain: 'Written, approved and taught; on most systems.' },
    { level: 4, name: 'Quantitatively managed', plain: 'On every system, automated, measured and reported.' },
    { level: 5, name: 'Optimizing', plain: 'Continuously improved and proven to work each year.' },
];
export const maturityLabel = (n) => (n === null || n === undefined ? 'Not rated' : `${n} ${MATURITY_LEVELS[n]?.name ?? ''}`.trim());
//# sourceMappingURL=plain-language.js.map